import { apiClient } from "@/lib/api";
import type { Changed } from "@/components/table/changedColumns";

// Warehouse → Bevestigingen (docs/wms-bevestigingen.md; docs/api.md §9.53): the inbound and outbound confirmations of
// Scoretrace orders to customers — what waits for its freight document, a deviation to approve, what went.

export type ConfirmationDirection = "inbound" | "outbound";
export type ConfirmationState = "waiting_document" | "deviation" | "queued" | "sent" | "failed";
export const CONFIRMATION_VIEWS = ["open", "sent", "all"] as const;
export type ConfirmationView = (typeof CONFIRMATION_VIEWS)[number];

export interface ConfirmationFacts {
  deliveryNoteNumber?: string | null;
  buyerOrderNumber?: string | null;
  vendorOrderNumber?: string | null;
  supplier?: string | null;
  containerNumber?: string | null;
  warehouseName?: string | null;
}

export interface WarehouseConfirmation extends Changed {
  id: string;
  direction: ConfirmationDirection;
  state: ConfirmationState;
  party: { id: string; code: string; name: string };
  ownerNumber: string;
  documentNumber: string;
  scoretraceOrderPk: string;
  readyAt: string;
  manco: boolean;
  blocked: boolean;
  facts: ConfirmationFacts;
  dossier: { id: string; number: string | null } | null;
  freightDocument: { id: string; fileName: string; documentType: string } | null;
  photo: { id: string; fileName: string; documentType: string } | null;
  approvedAt: string | null;
  approvedBy: { id: string; name: string } | null;
  remark: string | null;
  withoutDocumentAt: string | null;
  withoutDocumentBy: { id: string; name: string } | null;
  deviationMailAt: string | null;
  requestMailAt: string | null;
  queuedAt: string | null;
  sentAt: string | null;
  sentTo: string[];
  /** not_wanted, too_large, or what the cloud or the broker said. */
  error: string | null;
}

export const confirmationKeys = {
  all: ["warehouse", "confirmations"] as const,
  list: (view: ConfirmationView, q: string) => ["warehouse", "confirmations", view, q] as const,
};

export function listConfirmations(view: ConfirmationView, q: string): Promise<{ confirmations: WarehouseConfirmation[] }> {
  return apiClient.get("/api/v1/warehouse/confirmations", { view, ...(q ? { q } : {}) });
}

export function approveConfirmation(id: string, remark: string | null): Promise<{ id: string; state: ConfirmationState }> {
  return apiClient.post(`/api/v1/warehouse/confirmations/${id}/approve`, { remark });
}

export function sendWithoutDocument(id: string): Promise<{ id: string; state: ConfirmationState }> {
  return apiClient.post(`/api/v1/warehouse/confirmations/${id}/without-document`, {});
}

export function addConfirmationPhoto(id: string, file: File): Promise<{ documentId: string }> {
  const query = new URLSearchParams({ filename: file.name });
  return apiClient.postRaw(`/api/v1/warehouse/confirmations/${id}/photo?${query.toString()}`, file, file.type || "application/octet-stream");
}
